/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useContext, useEffect, useState } from "react";
import useError from "../../hooks/useError";
import { LayoutContext } from "../../components/layout";
import fetchHelper from "../../services/fetchHelper";
import PreAuthTable from "./PreAuthTable";
import { cpt30Pro, cpt90Pro, dateFormatForArrays, deviceType } from "../../utils/helpers";
import { preAuthHeaderCsv } from "../../constants/HeaderCsvConstants";
import "./AllReports.css";
import { CSVLink } from "react-csv";
import Pagination from "../../components/Pagination";

export const Loading = ({ length = 10, colspan = 17 }) => {
  const array = new Array(length);
  array.fill(1);
  return array.map((item, index) => (
    <tbody key={index}>
      <tr>
        <td colSpan={colspan}></td>
      </tr>
      <tr>
        <td colSpan={colspan} style={{ background: "#fff", padding: 0 }}>
          <img src="data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTUwNSA0NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KLnJlY3QgewpmaWxsOiB1cmwoI2xpbmVhci1ncmFkaWVudCk7Cn0KPC9zdHlsZT4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0ibGluZWFyLWdyYWRpZW50Ij4KPHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIzMyUiIHN0b3AtY29sb3I9IiNGN0Y3RjciIC8+CjxzdG9wIG9mZnNldD0iNTAlIiBzdG9wLWNvbG9yPSIjZWZmMGYwIiAvPgo8c3RvcCBvZmZzZXQ9IjY3JSIgc3RvcC1jb2xvcj0iI0Y3RjdGNyIgLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjRjdGN0Y3IiAvPgo8YW5pbWF0ZVRyYW5zZm9ybSBhdHRyaWJ1dGVOYW1lPSJncmFkaWVudFRyYW5zZm9ybSIKdHlwZT0idHJhbnNsYXRlIgpmcm9tPSItMSAwIgp0bz0iMSAwIgpiZWdpbj0iMHMiCmR1cj0iMS41cyIKcmVwZWF0Q291bnQ9ImluZGVmaW5pdGUiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgoKPHJlY3QgY2xhc3M9InJlY3QiIHg9IjAuOTgyOTEiIHk9IjAuMTY3NDgiIHdpZHRoPSIxNTAzLjE1IiBoZWlnaHQ9IjQ0LjgzMjUiIHJ4PSI2IiBmaWxsPSIjZWZmMGYwIi8+CjxyZWN0IGNsYXNzPSJyZWN0IiB4PSIwLjk4MjkxIiB5PSIwLjE2NzQ4IiB3aWR0aD0iMTUwMy4xNSIgaGVpZ2h0PSI0NC44MzI1IiByeD0iNiIgZmlsbD0idXJsKCNwYWludDBfbGluZWFyKSIvPgo8L3N2Zz4=" />
        </td>
      </tr>
    </tbody>
  ));
};

const AllReports = ({ activeTab: tab }) => {
  const { headerDetails, updateHeaderDetails, user } = useContext(LayoutContext);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const showDefaultApiError = useError();
  const limit = 30;
  const preAuthTab = 90;
  const [offset, setOffset] = useState(1);
  const [to, setTo] = useState(null);
  const [initOffest, setInitOffset] = useState(true);

  useEffect(() => {
    if (!initOffest) {
      setIsLoading(true);
      localStorage.setItem("patients-offset", offset);
      fetchData();
    }
  }, [offset]);

  useEffect(() => {
    setInitOffset(true);
    if (user) fetchData();
  }, [tab, user]);

  const fetchData = async () => {
    try {
      setIsLoading(true);

      const newOffset = Number(localStorage.getItem(`patients-offset`)) || 1;
      setOffset(newOffset);

      const data = await fetchHelper.get({
        url: `/patientmanagement/rest/PreAuthorizedPatient/${
          tab === "90" ? "90DayCycles" : "30DayCycles"
        }?locationId=${user?.locations[0].id}`,
        headers: {
          Range: `items=${newOffset}-${newOffset + (limit - 1)}`,
        },
      });

      setData(data);
      setTo(newOffset + data.length - 1);
      setInitOffset(false);
      updateHeaderDetails(data);
    } catch (err) {
      showDefaultApiError(err);
    } finally {
      setIsLoading(false);
      setInitOffset(false);
    }
  };

  const preAuthCsv =
    headerDetails &&
    headerDetails.map((item) => ({
      name: `${item.patientFamily}, ${item.patientName}`,
      dob: item.patientDOB ? new Date(item.patientDOB).toShortDateFormat() : "No data",
      mrn: item.patientMRN || "No data",
      deviceType: deviceType(item.deviceType) || "No data",
      payerType: item.insurancePayerType ? item.insurancePayerType.slice(0, 1) : "No data",
      insuranceType: item.insuranceType ? item.insuranceType : "No data",
      cptThree: `Pro: ${cpt30Pro(item?.deviceType, item?.remoteMonitoring30CycleEndDay)}, Tec: 93299`,
      threeDos: item?.remoteMonitoring30CycleEndDay
        ? new Date(item?.remoteMonitoring30CycleEndDay).addDays(1).toShortDateFormat()
        : "-",
      cptNine: `Pro: ${cpt90Pro(
        item?.deviceType,
        item?.remoteMonitoring90CycleEndDay,
        item?.remoteMonitoring30CycleEndDay
      )}, Tec: ${!item?.remoteMonitoring30CycleEndDay && item?.remoteMonitoring90CycleEndDay ? 93296 : "-"}`,
      nineDos: item?.remoteMonitoring90CycleEndDay
        ? new Date(item?.remoteMonitoring90CycleEndDay).addDays(1).toShortDateFormat()
        : "-",
      insuranceName: item.insuranceName ? item.insuranceName : "No data",
      policy: item.insurancePolicyNumber || "No data",
      group: item.insuranceTypeGroupNumber || "No data",
      preauthThree: item.remoteMonitoring30CyclePreAuth
        ? dateFormatForArrays(item.remoteMonitoring30CyclePreAuth)
        : "No data",
      preauthNine: item.remoteMonitoring90CyclePreAuth
        ? dateFormatForArrays(item.remoteMonitoring90CyclePreAuth)
        : "No data",
    }));

  return (
    <>
      {data.length && !isLoading ? (
        <div className="csv-div-preauth">
          <CSVLink
            data={preAuthCsv}
            headers={preAuthHeaderCsv}
            filename={tab == preAuthTab ? "Preauth90Report.csv" : "Preauth30Report.csv"}
            className="csv-link-preauth"
          >
            <button className="btn btnTeal hideAdv">Download CSV</button>
          </CSVLink>
        </div>
      ) : null}
      <PreAuthTable data={data} initialLoad={isLoading} activeTab={tab} />

      <Pagination offset={offset - 1} to={to - 1} limit={limit} onSetOffset={setOffset} loading={isLoading} />
    </>
  );
};

export default AllReports;
